import React from "react";
import { motion } from "motion/react";

const RevealText = ({ children, delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
};

const BentoCard = ({ children, className = "" }) => (
  <motion.div 
    initial={{ opacity: 0, scale: 0.95, y: 20 }}
    whileInView={{ opacity: 1, scale: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    whileHover={{ scale: 0.98 }}
    transition={{ duration: 0.5, ease: "easeOut" }}
    className={`bg-black border border-white/10 rounded-[1.5rem] md:rounded-[32px] p-6 sm:p-8 md:p-12 hover:border-white/20 hover:bg-[#050505] transition-colors ${className}`}
  >
    {children}
  </motion.div>
);

const AboutFozzil = () => {
  return (
    <div className="bg-black text-white font-secondary min-h-screen w-full selection:bg-blue-500 selection:text-white">
      
      {/* 1. HERO SECTION */}
      <section className="relative w-full h-[90vh] min-h-[600px] flex flex-col items-center justify-center overflow-hidden">
        {/* Minimalist Tech Background Aura */}
        <div className="absolute inset-0 z-0 bg-black flex items-center justify-center overflow-hidden">
          {/* Subtle slow pulsing glowing orb behind the text */}
          <motion.div 
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{ 
              duration: 8, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
            className="w-[150vw] h-[150vw] md:w-[800px] md:h-[800px] bg-blue-500 rounded-full blur-[120px] md:blur-[200px]"
          />
        </div>

        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 flex flex-col items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-center text-center w-full"
          >
            <div className="flex flex-col items-center max-w-4xl">
              <h1 className="font-primary text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] font-bold tracking-tight text-white m-0 text-center leading-[1.1] sm:leading-[1.1]">
                Future Oriented <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#065DC0] via-blue-400 to-[#065DC0]">
                  Zone-of Intelligent Life
                </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#065DC0] via-blue-400 to-[#065DC0]">.</span>
              </h1>
            </div>
            <div className="w-16 h-1 bg-white/20 rounded-full mt-10 mb-8" />
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="text-gray-400 font-medium max-w-2xl tracking-[0.2em] md:tracking-[0.25em] uppercase text-[10px] sm:text-xs md:text-sm drop-shadow-md px-4"
            >
              Building adaptive intelligence for humanity.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES SECTION */}
      <section className="relative z-10 bg-black w-full pb-32 px-6 pt-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-16 lg:gap-24 items-center">
            <RevealText>
              <h2 className="text-[#065DC0] font-semibold tracking-widest uppercase text-xs md:text-sm mb-4 md:mb-6">Our Services</h2>
              <h3 className="font-primary text-4xl sm:text-5xl md:text-6xl text-white font-bold tracking-tight leading-[1.1] mb-6 md:mb-8">
                Intelligent Evolution,<br className="hidden md:block"/> Engineered for You.
              </h3>
              <p className="text-lg sm:text-xl md:text-2xl text-gray-400 leading-relaxed font-light">
                At FOZZIL, we build adaptive intelligence that evolves with your business and, powered by ethical AGI, turns complexity into clarity—one smart step at a time.
              </p>
            </RevealText>
            
            <RevealText delay={0.2}>
              <div className="w-full aspect-video rounded-[1.5rem] md:rounded-[32px] overflow-hidden relative bg-black border-none">
                <iframe
                  title="fozzil-video"
                  src="https://drive.google.com/file/d/1bFi7GE7luIdF_nmOLzsOEjFa5MV6IhAv/preview"
                  className="w-full h-full object-cover"
                  allowFullScreen
                ></iframe>
              </div>
            </RevealText>
          </div>
        </div>
      </section>

      {/* 3. WHAT IS FOZZIL? (Bento Grid Layout) */}
      <section className="relative bg-black w-full py-20 md:py-32 border-t border-white/10 px-6">
        {/* Subtle Background Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[600px] md:h-[600px] bg-blue-500/5 blur-[120px] md:blur-[200px] rounded-full pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto">
          <RevealText>
            <h2 className="font-primary text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-10 md:mb-16 text-center">
              What is <span className="text-[#065DC0]">FOZZIL?</span>
            </h2>
          </RevealText>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-6">
            
            {/* Callout Card */}
            <BentoCard className="md:col-span-2 relative overflow-hidden !bg-transparent !border-none hover:!bg-transparent shadow-none px-0 sm:px-4">
              <div className="absolute -right-10 -top-10 md:-right-20 md:-top-20 w-40 h-40 md:w-80 md:h-80 bg-blue-500/10 blur-[80px] md:blur-[100px] rounded-full pointer-events-none" />
              <h4 className="relative z-10 text-2xl md:text-3xl font-bold text-white mb-4 md:mb-6 font-primary tracking-tight">Transformative Partner</h4>
              <p className="relative z-10 text-lg sm:text-xl text-gray-400 font-light leading-relaxed">
                <strong className="text-white font-normal">FOZZIL</strong> is an AI Research & Development Company pioneering adaptive, ethical, and human-centered Artificial General Intelligence. We advance AGI as a transformative partner in human progress—across healthcare, education, enterprise, and daily life—by creating intelligent systems that learn from real-world interactions and deliver scalable, context-aware solutions.
              </p>
            </BentoCard>

            {/* Accent Card */}
            <BentoCard className="md:col-span-1 !border-none hover:!border-none">
              <h4 className="text-2xl md:text-3xl font-bold text-[#065DC0] mb-4 md:mb-6 font-primary tracking-tight">Ethical Core</h4>
              <p className="text-base sm:text-lg text-gray-300 font-light leading-relaxed">
                <strong className="text-white font-medium">Ethical considerations</strong> at FOZZIL are embedded in every layer. We uphold transparency, fairness, and accountability, ensuring compliance and safeguarding data. Our systems augment human capabilities, fostering collaboration and well-being.
              </p>
            </BentoCard>

            {/* Bottom Wide Card */}
            <BentoCard className="md:col-span-3 flex flex-col md:flex-row gap-6 md:gap-10 items-start md:items-center !bg-transparent !border-none shadow-none mt-6 md:mt-10 px-0 sm:px-4">
              <div className="flex-shrink-0 w-16 h-16 md:w-24 md:h-24 rounded-[1rem] md:rounded-[1.25rem] bg-blue-500/5 border border-blue-500/20 flex items-center justify-center p-1.5">
                <div className="w-full h-full rounded-[1rem] md:rounded-32px opacity-80" style={{ background: "url('/logoicon.png') center/contain no-repeat" }} />
              </div>
              <div>
                <h4 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-4 font-primary tracking-tight flex flex-wrap items-center gap-2">
                  <span>Adaptive Intelligence</span>
                  <span className="text-xs font-mono font-medium text-blue-400 bg-blue-500/10 border border-blue-500/20 px-2.5 py-0.5 rounded-full">T.A.E.L.S.</span>
                </h4>
                <p className="text-lg sm:text-xl text-gray-400 font-light leading-relaxed">
                  Our team of AI researchers, data scientists, and domain experts drives responsible innovation through <strong>T.A.E.L.S. (Truly Adaptive, Efficient & Learning Synthesis)</strong>. Our Adaptive Intelligence is a unified, evolving cognitive layer that automates, learns, and connects across every device and system—seamlessly coordinating workflows and growing smarter with every interaction.
                </p>
              </div>
            </BentoCard>

          </div>
        </div>
      </section>

      {/* 4. MISSION & VISION */}
      <section className="relative bg-black w-full py-20 md:py-32 px-6">
        <div className="max-w-7xl mx-auto flex flex-col gap-24 md:gap-40">
          
          {/* Mission */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-center">
            <div className="lg:col-span-7">
              <RevealText>
                <h2 className="text-[#065DC0] font-semibold tracking-widest uppercase text-xs md:text-sm mb-4 md:mb-6">Our Mission</h2>
                <h3 className="font-primary text-4xl sm:text-5xl md:text-6xl lg:text-[4rem] text-white font-bold tracking-tight leading-[1.05] mb-6 md:mb-8">
                  Building the world’s first true Artificial General Intelligence that learns from humanity.
                </h3>
                <p className="text-lg sm:text-xl md:text-2xl text-gray-400 font-light leading-relaxed">
                  It grows with humanity, and empowers every system, organization, and individual to think smarter, act faster, and live the best — in real time, across every domain.
                </p>
              </RevealText>
            </div>
            
            <div className="lg:col-span-5 flex flex-col gap-6">
              <RevealText delay={0.2}>
                <div className="p-8 sm:p-10 md:p-12 rounded-[1.5rem] md:rounded-[32px] bg-black border border-white/10 hover:border-white/20 hover:bg-[#050505] transition-colors">
                  <p className="text-lg sm:text-xl text-gray-400 font-light leading-relaxed">
                    Featured with <strong className="text-[#065DC0] font-normal">Adaptive Intelligence and Self-Learning Systems</strong>. This isn’t just about smarter algorithms—it's about a single, evolving intelligence that learns, transfers knowledge, and grows in real time like a mind that never stops developing.
                  </p>
                </div>
              </RevealText>
            </div>
          </div>

          {/* Vision */}
          <div className="flex flex-col gap-12 md:gap-16">
            <RevealText>
              <div className="text-center max-w-5xl mx-auto">
                <h2 className="text-[#065DC0] font-semibold tracking-widest uppercase text-xs md:text-sm mb-4 md:mb-6">Our Vision</h2>
                <h3 className="font-primary text-4xl sm:text-5xl md:text-7xl text-white font-bold tracking-tight leading-[1.05]">
                  Train Human Intelligence to work smarter through AGI.
                </h3>
              </div>
            </RevealText>
            
            <RevealText>
              <div className="w-full aspect-square sm:aspect-[4/3] md:aspect-[3/1] rounded-[1.5rem] md:rounded-[32px] overflow-hidden relative border border-white/10 group">
                <div className="absolute inset-0 bg-black/40 z-10 transition-opacity duration-500 group-hover:opacity-10 pointer-events-none" />
                <div 
                  className="w-full h-full bg-cover bg-center transition-transform duration-[15s] ease-linear group-hover:scale-110"
                  style={{ backgroundImage: "url('/solution.png')" }}
                />
              </div>
            </RevealText>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-24 max-w-6xl mx-auto px-0 sm:px-4">
              <RevealText>
                <p className="text-lg sm:text-xl md:text-2xl text-gray-300 font-light leading-[1.6]">
                  We develop AGI systems that elevate human potential, enhance productivity, and foster seamless collaboration between humans and machines. AGI as an ally in human progress, expanding knowledge and enabling smarter decisions.
                </p>
              </RevealText>
              <RevealText delay={0.2}>
                <p className="text-lg sm:text-xl md:text-2xl text-gray-400 font-light leading-[1.6]">
                  We see AGI innovations reshaping industries and empowering businesses and lives. Our mission: redefine what's possible in a smarter, more connected world.
                </p>
              </RevealText>
            </div>

            <RevealText>
              <div className="flex justify-center mt-20 pb-20">
                <div 
                  className="w-48 h-12 opacity-80"
                  style={{ background: "url('/logo.png') center/contain no-repeat" }}
                />
              </div>
            </RevealText>
            
          </div>

        </div>
      </section>

    </div>
  );
};

export default AboutFozzil;
